import React, { useState, useEffect, useCallback, useRef } from 'react';
import { StatusBadge, statusTone } from '@/design-system/dna';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageHeader } from '@/components/ui-dna';
import {
  User, Phone, Mail, Calendar, GraduationCap, Wallet,
  MessageSquare, FileText, Clock, ChevronRight, Edit, Search, ShieldAlert,
  Send, AlertCircle, CheckCircle2, TrendingUp, TrendingDown,
  BookOpen, Users, CreditCard, Receipt, Bell, Eye, RefreshCw,
  ArrowRight, ArrowRightLeft, History, Sparkles, Activity, DoorOpen, Shield, Save, Loader2, UserPlus, Power, Home, AlertTriangle
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import AlunoFinanceiroTab from './AlunoFinanceiroTab';
import { usePermV2 } from '@/lib/perm_v2';
import OcorrenciasPanel from '@/pages/PortalGestao/OcorrenciasPanel';
import VinculosAnuais from './VinculosAnuais';
import StudentSinglePage from './StudentSinglePage';
import GuardiansLinkedList from './GuardiansLinkedList';
import FamiliaBar from './FamiliaBar';
import SmartImportModal from '@/pages/Matriculas/SmartImportModal';
import CompletudeBadge from './CompletudeBadge';
import { GoogleAcessoCard } from './GoogleAcessoCard';
import { GoogleLoteButton } from './GoogleLoteDialog';
import DocumentosCard from './DocumentosCard';
import DocumentacaoResumo from './DocumentacaoResumo';
import { SensitiveField } from '@/pages/PortalGestao/usePortalGestao';

const API_URL = window.location.origin || '';
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } };
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] } } };
const fmtCur = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';

// ── STATUS PILL ─────────────────────────────

const PILL_TONE = { green: 'success', amber: 'warning', red: 'danger', gray: 'neutral' };
const StatusPill = ({ label, color }) => <StatusBadge tone={statusTone(label) || PILL_TONE[color]} testid="aluno360-status">{label}</StatusBadge>;

const formatWaLink = (phone) => {
  if (!phone) return null;
  const nums = phone.replace(/\D/g, '');
  const full = nums.length <= 11 ? `55${nums}` : nums;
  return `https://wa.me/${full}`;
};

const RespCard = ({ resp, tipo, bgClass, labelClass }) => {
  if (!resp) return null;
  const waLink = formatWaLink(resp.phone || resp.cellphone || resp.whatsapp);
  return (
    <div className={`${bgClass} rounded-xl p-3 space-y-2`}>
      <div className="flex items-center justify-between">
        <p className={`text-[10px] font-bold uppercase tracking-wider ${labelClass}`}>{tipo}</p>
        {waLink && (
          <a href={waLink} target="_blank" rel="noopener noreferrer" title="WhatsApp"
            className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500 hover:bg-green-600 text-white text-[9px] font-semibold transition-colors shadow-sm">
            <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.146.56 4.26 1.621 6.109L.002 24l6.076-1.594A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-1.98 0-3.92-.53-5.61-1.533l-.403-.24-3.6.944.96-3.51-.262-.417A9.72 9.72 0 012.25 12C2.25 6.623 6.623 2.25 12 2.25S21.75 6.623 21.75 12 17.377 21.75 12 21.75z"/></svg>
            WhatsApp
          </a>
        )}
      </div>
      <p className="text-sm font-semibold text-slate-800">{resp.name}</p>
      <div className="flex flex-wrap items-center gap-2">
        {(resp.phone || resp.cellphone) ? (
          <a href={`tel:${resp.phone || resp.cellphone}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/80 border border-slate-200 text-[10px] text-slate-600 hover:bg-white transition-colors">
            <Phone className="w-2.5 h-2.5" />
            <SensitiveField verb="telefone" value={resp.phone || resp.cellphone} onceKey={`resp-phone-${resp.id || resp.name}`} targetType="responsavel" targetId={resp.id} />
          </a>
        ) : (
          <span className="text-[10px] text-slate-400 italic">Telefone nao informado</span>
        )}
        {resp.email ? (
          <a href={`mailto:${resp.email}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/80 border border-slate-200 text-[10px] text-slate-600 hover:bg-white transition-colors truncate max-w-[180px]">
            <Mail className="w-2.5 h-2.5 shrink-0" />{resp.email}
          </a>
        ) : (
          <span className="text-[10px] text-slate-400 italic">E-mail nao informado</span>
        )}
      </div>
    </div>
  );
};


// ── QUICK ACTION BTN ─────────────────────────

const QuickAction = ({ icon: Icon, label, color, onClick }) => (
  <button onClick={onClick} data-testid={`action-${label?.toLowerCase().replace(/\s/g, '-')}`}
    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-100 hover:shadow-md hover:-translate-y-[1px] transition-all">
    <div className="p-1 rounded-lg" style={{ background: `${color}12` }}>
      <Icon className="h-3.5 w-3.5" style={{ color }} />
    </div>
    <span className="text-[12px] font-medium text-slate-600">{label}</span>
  </button>
);

// ── STATUS CARD ──────────────────────────────

const StatusCard = ({ label, value, icon: Icon, color, sub }) => (
  <motion.div variants={fadeUp}
    className="rounded-xl p-4 border" style={{ background: `${color}06`, borderColor: `${color}15` }}>
    <div className="flex items-center gap-2 mb-2">
      <div className="p-1.5 rounded-lg" style={{ background: `${color}12` }}>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-xl font-bold text-slate-800">{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </motion.div>
);

// ── SECTION HEADER ───────────────────────────

const SectionHeader = ({ icon: Icon, title, color, action, onAction }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2">
      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
      <Icon className="h-4 w-4" style={{ color }} />
      <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400">{title}</span>
    </div>
    {action && (
      <button onClick={onAction} className="flex items-center gap-1 text-[11px] font-semibold hover:opacity-70 transition-opacity" style={{ color }}>
        {action} <ChevronRight className="h-3 w-3" />
      </button>
    )}
  </div>
);

// ── INSIGHT ITEM ─────────────────────────────

const InsightItem = ({ text, action, color, onClick }) => (
  <button onClick={onClick}
    className="w-full p-3 rounded-xl text-left border-l-[3px] hover:translate-x-0.5 transition-all"
    style={{ borderLeftColor: color, background: `${color}06` }}>
    <p className="text-[12px] text-slate-600">{text}</p>
    {action && <span className="text-[11px] font-semibold mt-1 flex items-center gap-1" style={{ color }}>{action} <ArrowRight className="h-3 w-3" /></span>}
  </button>
);

// ── TIMELINE ITEM ────────────────────────────

const TimelineItem = ({ icon: Icon, title, date, color }) => (
  <div className="flex items-start gap-3 relative">
    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border border-slate-100" style={{ background: `${color}10` }}>
      <Icon className="h-3.5 w-3.5" style={{ color }} />
    </div>
    <div className="flex-1 pb-4 border-l border-slate-100 pl-4 -ml-[2px]">
      <p className="text-[12px] font-medium text-slate-700">{title}</p>
      <p className="text-[10px] text-slate-400">{fmtDate(date)}</p>
    </div>
  </div>
);

// ═══════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════

export default function Aluno360() {
  const { alunoId } = useParams();
  const { financeiroVisivel } = usePermV2();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [alunos, setAlunos] = useState([]);
  const [aluno, setAluno] = useState(null);
  const [data360, setData360] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Busca única (aluno + matrícula + turma + responsável) — fonte canônica no backend
  const [remoteIds, setRemoteIds] = useState(null);
  const [remoteResp, setRemoteResp] = useState({});
  const [showSearch, setShowSearch] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showSmartImport, setShowSmartImport] = useState(false);
  const [compRefresh, setCompRefresh] = useState(0);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // New student state — wizard FASE 1 (form interno mora dentro do StudentWizard)
  const [showNewStudent, setShowNewStudent] = useState(false);
  // Bulk apply student-address → responsibles
  const [showApplyAddress, setShowApplyAddress] = useState(false);
  const [bulkScope, setBulkScope] = useState('all'); // all | filtered
  const [bulkOnlyEmpty, setBulkOnlyEmpty] = useState(true);
  const [bulkPreview, setBulkPreview] = useState(null); // {examined, updated, skipped...}
  const [bulkBusy, setBulkBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState('todos'); // 'todos' | 'ativos' | 'inativos'
  const [togglingId, setTogglingId] = useState(null);

  const [classGroups, setClassGroups] = useState([]);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferFormData, setTransferFormData] = useState({ new_class_group_id: '', new_call_number: '', keep_grades: true, keep_attendance: true, reason: '' });
  const [transferLoading, setTransferLoading] = useState(false);
  // History states
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Load students list + responsibles
  const [responsavelMap, setResponsavelMap] = useState({});
  const [selectedYear, setSelectedYear] = useState(null); // V7.3 — ano letivo escolhido (notas/faltas/boletim)
  const [responsibles, setResponsibles] = useState([]);
  const loadResponsibles = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await fetch(`${API_URL}/api/responsibles`, { headers });
      const resps = await r.json();
      const list = Array.isArray(resps) ? resps : [];
      setResponsibles(list);
      const rMap = {};
      list.forEach(rr => { rMap[rr.id] = rr; });
      setResponsavelMap(rMap);
    } catch { /* noop */ }
  }, []);
  // Busca no servidor com debounce: cobre responsável via guardian_student_links (irmãos)
  useEffect(() => {
    const term = searchQuery.trim();
    if (term.length < 2) { setRemoteIds(null); setRemoteResp({}); return; }
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${API_URL}/api/v2/aluno/search?q=${encodeURIComponent(term)}&limit=60`, { headers });
        const arr = await r.json();
        const list = Array.isArray(arr) ? arr : [];
        setRemoteIds(new Set(list.map(x => x.id)));
        setRemoteResp(Object.fromEntries(list.filter(x => x.responsible_name).map(x => [x.id, x.responsible_name])));
      } catch { setRemoteIds(null); }
    }, 260);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const loadAlunos = useCallback(async () => {    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [sRes, rRes] = await Promise.all([
        fetch(`${API_URL}/api/students`, { headers }),
        fetch(`${API_URL}/api/responsibles`, { headers })
      ]);
      const students = await sRes.json();
      const resps = await rRes.json();
      setAlunos(Array.isArray(students) ? students : []);
      const list = Array.isArray(resps) ? resps : [];
      setResponsibles(list);
      const rMap = {};
      list.forEach(r => { rMap[r.id] = r; });
      setResponsavelMap(rMap);
    } catch { /* noop */ }
  }, []);

  // Load 360 data from unified V2 API
  const load360 = useCallback(async (student) => {
    if (!student) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/v2/aluno/${student.id}`, { headers });
      const d = await res.json();
      setData360(d.error ? null : d);
    } catch { setData360(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAlunos(); }, [loadAlunos]);

  // Load class groups for transfer
  useEffect(() => {
    const loadClassGroups = async () => {
      try {
        const token = localStorage.getItem('token');
        const h = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${API_URL}/api/class-groups`, { headers: h });
        const d = await res.json();
        setClassGroups(Array.isArray(d) ? d : []);
      } catch {}
    };
    loadClassGroups();
  }, []);

  // Transfer handlers
  const openTransferDialog = () => {
    if (!aluno) return;
    setTransferFormData({ new_class_group_id: '', new_call_number: '', keep_grades: true, keep_attendance: true, reason: '' });
    setTransferDialogOpen(true);
  };

  const handleTransferStudent = async () => {
    if (!transferFormData.new_class_group_id) { toast.error('Selecione a turma de destino'); return; }
    if (transferFormData.new_class_group_id === aluno.class_group_id) { toast.error('Selecione uma turma diferente da atual'); return; }
    setTransferLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { new_class_group_id: transferFormData.new_class_group_id, keep_grades: transferFormData.keep_grades, keep_attendance: transferFormData.keep_attendance };
      if (transferFormData.new_call_number) payload.new_call_number = parseInt(transferFormData.new_call_number);
      if (transferFormData.reason?.trim()) payload.reason = transferFormData.reason.trim();
      const res = await fetch(`${API_URL}/api/students/${aluno.id}/transfer`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Erro');
      toast.success(d.message || 'Aluno transferido');
      setTransferDialogOpen(false);
      loadAlunos();
      setTimeout(() => { if (aluno) load360(aluno); }, 500);
    } catch (e) { toast.error(e.message || 'Erro ao transferir'); }
    finally { setTransferLoading(false); }
  };

  const openHistoryDialog = async () => {
    if (!aluno) return;
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/students/${aluno.id}/transfer-history`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      setTransferHistory(d.transfers || []);
    } catch { setTransferHistory([]); toast.error('Erro ao carregar historico'); }
    finally { setHistoryLoading(false); }
  };

  const fmtTransferDate = (d) => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }) : '-';

  const loaded360Ref = useRef(null);
  useEffect(() => {
    if (alunoId && alunos.length > 0) {
      const found = alunos.find(a => a.id === alunoId);
      if (!found) return;
      setAluno(found);
      // V9.7 · recarrega o 360 só quando o aluno muda (antes qualquer refresh da lista
      // remontava a página e derrubava o cadastro aberto)
      if (loaded360Ref.current !== found.id) {
        loaded360Ref.current = found.id;
        load360(found);
      }
    } else if (!alunoId) {
      setLoading(false);
      setShowSearch(true);
    }
  }, [alunoId, alunos, load360]);

  const selectAluno = (a) => {
    setAluno(a); setShowSearch(false);
    navigate(`/v2/aluno/${a.id}`);
    load360(a);
  };

  const openEdit = () => {
    if (!aluno || !data360) return;
    setEditForm({
      name: data360.name || aluno.name || '',
      email: data360.email || aluno.email || '',
      phone: data360.phone || aluno.phone || '',
      birth_date: data360.birth_date || aluno.birth_date || '',
      gender: data360.gender || aluno.gender || '',
      situation: data360.situation || aluno.situation || 'Matriculado',
      registration_number: data360.registration_number || aluno.registration_number || '',
      class_group_id: data360.class_group_id || aluno.class_group_id || '',
      responsible_id: data360.responsible_id || aluno.responsible_id || '',
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/students/${aluno.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(editForm),
      });
      if (res.ok) {
        toast.success('Aluno atualizado com sucesso');
        setShowEdit(false);
        load360(aluno);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao salvar');
      }
    } catch { toast.error('Erro de conexão'); }
    finally { setSaving(false); }
  };

  const isAlunoActive = (a) => (a?.situation || '').toLowerCase().includes('matric') || !!a?.active;

  const handleToggleActive = async (a, e) => {
    e?.stopPropagation();
    if (!a?.id || togglingId) return;
    const currentlyActive = isAlunoActive(a);
    const newSituation = currentlyActive ? 'Inativo' : 'Matriculado';
    setTogglingId(a.id);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...a,
        situation: newSituation,
        active: !currentlyActive,
        class_name: a.class_name || '',
        year: a.year || new Date().getFullYear(),
        registration_number: a.registration_number || '',
        responsible_id: a.responsible_id || '',
      };
      const res = await fetch(`${API_URL}/api/students/${a.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(currentlyActive ? 'Aluno marcado como Inativo' : 'Aluno reativado');
        setAlunos(prev => prev.map(x => x.id === a.id ? { ...x, situation: newSituation, active: !currentlyActive } : x));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail ? (typeof err.detail === 'string' ? err.detail : 'Dados inválidos') : 'Erro ao atualizar');
      }
    } catch { toast.error('Erro de conexão'); }
    finally { setTogglingId(null); }
  };

  const filtered = alunos.filter(a => {
    const q = searchQuery.trim().toLowerCase();
    const qDigits = q.replace(/\D/g, '');
    const resps = [responsavelMap[a.responsible_id], responsavelMap[a.responsible_didactic_id],
                    responsavelMap[a.responsible_financial_optional_id]].filter(Boolean);
    const matchResp = !!q && resps.some(r => (
      (r.name || '').toLowerCase().includes(q)
      || (r.email || '').toLowerCase().includes(q)
      || (qDigits.length >= 3 && [r.cpf, r.phone, r.cellphone, r.whatsapp, r.phone_normalized]
        .some(v => (v || '').replace(/\D/g, '').includes(qDigits)))
    ));
    const matchSearch = !q
      || (a.name || '').toLowerCase().includes(q)
      || (a.registration_number || '').includes(q)
      || (a.class_name || '').toLowerCase().includes(q)
      || matchResp
      || (remoteIds ? remoteIds.has(a.id) : false);
    if (!matchSearch) return false;
    if (statusFilter === 'ativos') return isAlunoActive(a);
    if (statusFilter === 'inativos') return !isAlunoActive(a);
    return true;
  });

  // Derived data
  const allNotas = data360?.notas || [];
  const allFreq = data360?.frequencia || [];
  const matchYear = (v) => selectedYear == null || String(v ?? '') === String(selectedYear);
  const notas = allNotas.filter((n) => matchYear(n.year));
  const freq = allFreq.filter((f) => matchYear(f.year));
  const fin = data360?.financeiro || {};
  const titulos = fin.titulos || [];
  const resp = data360?.responsavel;
  const responsaveis = data360?.responsaveis || (resp ? [{...resp, role: 'Responsável Financeiro'}] : []);
  const turma = data360?.turma;
  const dnago = data360?.dnago || {};
  const dnaconnect = data360?.dnaconnect || {};
  const timeline = data360?.timeline || [];

  const mediaGeral = notas.length > 0 ? (notas.reduce((s, n) => s + (n.value || 0), 0) / notas.length).toFixed(1) : '-';
  const freqPresente = freq.filter(f => f.status === 'presente').length;
  const freqTotal = freq.length;
  const freqPct = freqTotal > 0 ? Math.round(freqPresente / freqTotal * 100) : '-';
  const situation = aluno?.situation || 'Matriculado';

  // Insights
  const insights = [];
  if (financeiroVisivel && fin.vencidos > 0) insights.push({ text: `${fin.vencidos} títulos vencidos. Risco de inadimplência.`, action: 'Ver financeiro', color: '#E11D48', path: '/v2/financeiro' });
  if (notas.length > 0 && parseFloat(mediaGeral) < 6) insights.push({ text: `Média geral ${mediaGeral} — abaixo do ideal.`, action: 'Ver notas', color: '#D97706', path: '/v2/academico/notas' });
  if (typeof freqPct === 'number' && freqPct < 80) insights.push({ text: `Frequência ${freqPct}% — risco de reprovação.`, action: 'Notificar responsável', color: '#EF4444' });
  if (dnago.ocorrencias?.length > 0) insights.push({ text: `${dnago.ocorrencias.length} ocorrências registradas no DNA GO.`, action: 'Ver ocorrências', color: '#D97706', path: '/v2/operacao/ocorrencias' });

  // ── EMPTY / SEARCH STATE ───────────────────

  if (!aluno && !loading) {
    return (
      <>
      <motion.div initial="hidden" animate="visible" variants={stagger} data-testid="aluno360-search-state" className="space-y-5">
        <PageHeader
          icon={UserPlus}
          eyebrow="DNACore V2"
          title="Aluno 360"
          subtitle="Selecione um aluno para ver a visão completa, ou cadastre um novo"
          rightSlot={
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowApplyAddress(true)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-white/15 hover:bg-white/25 text-white border border-white/20 backdrop-blur-sm font-semibold text-xs px-3.5 py-2 transition-colors"
                data-testid="btn-aplicar-endereco-massa"
                title="Aplicar endereço do aluno aos responsáveis sem endereço"
              >
                <Home className="w-3.5 h-3.5" /> Aplicar Endereço
              </button>
              <GoogleLoteButton />
              <button
                onClick={() => setShowNewStudent(true)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-white text-violet-700 hover:bg-violet-50 font-bold text-xs px-3.5 py-2 shadow-md"
                data-testid="btn-novo-aluno"
              >
                <UserPlus className="w-3.5 h-3.5" /> Novo Aluno
              </button>
            </div>
          }
        />

        <motion.div variants={fadeUp} className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="aluno-search-input" placeholder="Buscar aluno, matrícula, turma ou responsável..."
            className="pl-10 rounded-xl" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </motion.div>

        <motion.div variants={fadeUp} className="flex items-center gap-2 text-xs">
          {[
            { key: 'todos', label: 'Todos' },
            { key: 'ativos', label: 'Ativos' },
            { key: 'inativos', label: 'Inativos' },
          ].map(opt => (
            <button
              key={opt.key}
              data-testid={`filter-status-${opt.key}`}
              onClick={() => setStatusFilter(opt.key)}
              className={`px-3 py-1.5 rounded-full font-semibold border transition-colors ${
                statusFilter === opt.key
                  ? 'bg-violet-600 text-white border-violet-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'
              }`}
            >
              {opt.label}
              {opt.key !== 'todos' && (
                <span className="ml-1.5 opacity-70">
                  ({alunos.filter(a => opt.key === 'ativos' ? isAlunoActive(a) : !isAlunoActive(a)).length})
                </span>
              )}
            </button>
          ))}
        </motion.div>

        {filtered.length === 0 ? (
          <p className="p-12 text-center text-sm text-slate-400">Nenhum aluno encontrado</p>
        ) : (
          <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.slice(0, 30).map(a => {
              const respFin = responsavelMap[a.responsible_id];
              const respPed = responsavelMap[a.responsible_didactic_id];
              const isActive = isAlunoActive(a);
              return (
                <div key={a.id} className="bg-white rounded-xl border border-slate-200 hover:border-violet-300 hover:shadow-md transition-all p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => selectAluno(a)}>
                      <h3 className="font-semibold text-slate-800 truncate">{a.name}</h3>
                      <p className="text-xs text-slate-500">{a.registration_number || '-'} {a.class_name ? `• ${a.class_name}` : ''} {a.year ? `- ${a.year}` : ''}</p>
                      {(remoteResp[a.id] || respFin?.name) && (
                        <p className="text-xs text-violet-600 truncate" data-testid={`aluno-resp-${a.id}`}>
                          Responsável: {remoteResp[a.id] || respFin?.name}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <StatusPill label={isActive ? 'Ativo' : (a.situation || 'Inativo')} color={isActive ? 'green' : 'gray'} />
                      <button onClick={() => selectAluno(a)} title="Aluno 360" className="p-1 rounded hover:bg-violet-50"><Eye className="w-4 h-4 text-violet-500" /></button>
                      <button onClick={() => { setEditForm({...a}); setShowEdit(true); }} title="Editar" className="p-1 rounded hover:bg-slate-100" data-testid={`btn-edit-${a.id}`}><Edit className="w-4 h-4 text-slate-400" /></button>
                      <button onClick={() => { setAluno(a); openHistoryDialog(); }} title="Transferencias" className="p-1 rounded hover:bg-slate-100"><ArrowRightLeft className="w-4 h-4 text-orange-400" /></button>
                      <button
                        onClick={(e) => handleToggleActive(a, e)}
                        disabled={togglingId === a.id}
                        title={isActive ? 'Inativar aluno' : 'Reativar aluno'}
                        data-testid={`btn-toggle-active-${a.id}`}
                        className={`p-1 rounded ${isActive ? 'hover:bg-rose-50' : 'hover:bg-emerald-50'} disabled:opacity-50`}
                      >
                        <Power className={`w-4 h-4 ${isActive ? 'text-rose-500' : 'text-emerald-500'}`} />
                      </button>
                    </div>
                  </div>
                  {/* Responsável Financeiro */}
                  <RespCard resp={respFin} tipo={financeiroVisivel ? "Responsavel Financeiro" : "Responsavel"} bgClass="bg-violet-50/80" labelClass="text-violet-600" />
                  {/* Responsável Pedagógico */}
                  {respPed && respPed.id !== respFin?.id && (
                    <RespCard resp={respPed} tipo="Responsavel Pedagogico" bgClass="bg-emerald-50/80" labelClass="text-emerald-600" />
                  )}
                </div>
              );
            })}
          </motion.div>
        )}
      </motion.div>

      {/* Cadastro em tela única — Novo + Editar */}
      <StudentSinglePage
        open={showNewStudent}
        onOpenChange={setShowNewStudent}
        mode="new"
        classGroups={classGroups}
        responsibles={responsibles}
        onSaved={() => { loadAlunos(); }}
        onResponsiblesUpdate={loadResponsibles}
      />
      <StudentSinglePage
        open={showEdit}
        onOpenChange={setShowEdit}
        mode="edit"
        student={editForm}
        classGroups={classGroups}
        responsibles={responsibles}
        onSaved={() => { loadAlunos(); }}
        onResponsiblesUpdate={loadResponsibles}
      />

      {/* ─── Aplicar endereço do aluno aos responsáveis (bulk) ─── */}
      <Dialog open={showApplyAddress} onOpenChange={(o) => !o && !bulkBusy && (setShowApplyAddress(false), setBulkPreview(null))}>
        <DialogContent className="max-w-lg" data-testid="bulk-apply-addr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-violet-600" />
              Aplicar endereço do aluno aos responsáveis
            </DialogTitle>
            <DialogDescription className="text-xs">
              Para cada aluno com endereço completo, copia para os responsáveis vinculados que estiverem sem endereço.
              <strong className="text-emerald-700"> Não sobrescreve</strong> responsáveis que já têm endereço completo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Escopo</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => { setBulkScope('all'); setBulkPreview(null); }}
                  className={`text-left rounded-lg border-2 px-3 py-2 transition-colors ${bulkScope === 'all' ? 'border-violet-400 bg-violet-50' : 'border-slate-200 bg-white hover:border-violet-200'}`}
                  data-testid="bulk-scope-all"
                >
                  <p className="text-sm font-bold text-slate-800">Todos os alunos ativos</p>
                  <p className="text-[11px] text-slate-500">Toda a base atual</p>
                </button>
                <button
                  type="button"
                  onClick={() => { setBulkScope('filtered'); setBulkPreview(null); }}
                  className={`text-left rounded-lg border-2 px-3 py-2 transition-colors ${bulkScope === 'filtered' ? 'border-violet-400 bg-violet-50' : 'border-slate-200 bg-white hover:border-violet-200'}`}
                  data-testid="bulk-scope-filtered"
                  disabled
                  title="Filtros virão em uma próxima iteração — por ora, use 'Todos os alunos'."
                >
                  <p className="text-sm font-bold text-slate-400">Apenas filtrados</p>
                  <p className="text-[11px] text-slate-400">(em breve)</p>
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" checked={bulkOnlyEmpty} onChange={(e) => { setBulkOnlyEmpty(e.target.checked); setBulkPreview(null); }} data-testid="bulk-only-empty" />
              Aplicar somente em responsáveis sem endereço completo
            </label>
            {bulkPreview && (
              <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3 text-xs space-y-0.5">
                <p className="font-bold text-violet-900 mb-1">Pré-visualização</p>
                <p>Alunos examinados: <strong>{bulkPreview.examined_students}</strong></p>
                <p>Responsáveis a atualizar: <strong className="text-emerald-700">{bulkPreview.updated_responsibles}</strong></p>
                <p>Ignorados — aluno sem endereço: <strong className="text-slate-600">{bulkPreview.skipped_no_student_address}</strong></p>
                <p>Ignorados — responsável já tem endereço: <strong className="text-slate-600">{bulkPreview.skipped_responsible_has_address}</strong></p>
                {!!bulkPreview.errors?.length && <p className="text-rose-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Erros: {bulkPreview.errors.length}</p>}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={bulkBusy} onClick={() => { setShowApplyAddress(false); setBulkPreview(null); }}>Cancelar</Button>
            <Button
              variant="outline"
              className="border-violet-200 text-violet-700 hover:bg-violet-50"
              disabled={bulkBusy}
              data-testid="bulk-preview-btn"
              onClick={async () => {
                setBulkBusy(true);
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API_URL}/api/students/responsibles/apply-student-address?dry_run=true`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ scope: bulkScope, only_responsibles_without_address: bulkOnlyEmpty }),
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data.detail || 'Erro');
                  setBulkPreview(data);
                } catch (e) { toast.error(e.message); }
                finally { setBulkBusy(false); }
              }}
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              Pré-visualizar
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700"
              disabled={bulkBusy || !bulkPreview || (bulkPreview?.updated_responsibles || 0) === 0}
              data-testid="bulk-confirm-btn"
              onClick={async () => {
                setBulkBusy(true);
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API_URL}/api/students/responsibles/apply-student-address?dry_run=false`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ scope: bulkScope, only_responsibles_without_address: bulkOnlyEmpty }),
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data.detail || 'Erro');
                  toast.success(`${data.updated_responsibles} responsável(is) atualizado(s)`);
                  setShowApplyAddress(false);
                  setBulkPreview(null);
                  loadResponsibles();
                } catch (e) { toast.error(e.message); }
                finally { setBulkBusy(false); }
              }}
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  // ── LOADING ────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse" data-testid="aluno360-loading">
        <div className="h-32 rounded-2xl bg-slate-100" />
        <div className="grid grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-slate-100" />)}</div>
        <div className="grid grid-cols-2 gap-4">{[...Array(2)].map((_, i) => <div key={i} className="h-52 rounded-xl bg-slate-100" />)}</div>
      </div>
    );
  }

  // ── FULL 360 VIEW ──────────────────────────

  return (
    <motion.div className="space-y-5" initial="hidden" animate="visible" variants={stagger} data-testid="aluno360-panel">

      {/* 0. FAMÍLIA — irmãos matriculados, 1 clique para alternar */}
      <FamiliaBar studentId={aluno?.id} studentName={aluno?.name}
        onSelect={(s) => selectAluno({ ...s, id: s.id, name: s.name })} />

      {/* 1. IDENTITY HEADER — Layout igual Students.js */}
      <motion.div variants={fadeUp} className="rounded-2xl p-6 bg-white border border-slate-100 shadow-sm">
        {/* Row 1: Name + Status + Action Icons */}
        <div className="flex items-start justify-between mb-1">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-800">{aluno?.name || 'Aluno'}</h1>
              <StatusPill label={situation === 'Matriculado' ? 'Matriculado' : (data360?.active ? 'Ativo' : situation)} color={situation === 'Matriculado' || data360?.active ? 'green' : situation === 'Inativo' ? 'red' : 'amber'} />
              <CompletudeBadge studentId={aluno?.id} refreshKey={compRefresh} />
              <div className="flex items-center gap-1">
                {financeiroVisivel && (
                <button onClick={() => navigate('/v2/financeiro/titulos')} title="Financeiro" className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                  <CreditCard className="h-5 w-5 text-emerald-600" />
                </button>
                )}
                <button onClick={() => load360(aluno)} title="Atualizar" className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                  <RefreshCw className="h-5 w-5 text-blue-600" />
                </button>
                <button onClick={openHistoryDialog} title="Historico de Transferencias" className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors" data-testid="btn-history-transfer">
                  <History className="h-5 w-5 text-blue-600" />
                </button>
                <button onClick={openTransferDialog} title="Transferir de Turma" className="p-1.5 rounded-lg hover:bg-orange-50 transition-colors" data-testid="btn-transfer-student">
                  <ArrowRightLeft className="h-5 w-5 text-orange-500" />
                </button>
                <button onClick={() => setShowSmartImport(true)} title="Importar documentos com IA" className="p-1.5 rounded-lg hover:bg-violet-50 transition-colors" data-testid="btn-smart-import-aluno360">
                  <Sparkles className="h-5 w-5 text-violet-600" />
                </button>
                <button onClick={() => { setEditForm({...aluno}); setShowEdit(true); }} title="Editar" className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="action-editar">
                  <Edit className="h-5 w-5 text-slate-700" />
                </button>
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-1">{aluno?.registration_number || ''} {aluno?.registration_number ? '•' : ''}</p>
          </div>
          <button onClick={() => { setAluno(null); setData360(null); navigate('/v2/aluno'); }}
            className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 ml-4 flex-shrink-0">
            <Search className="h-3.5 w-3.5" /> Trocar
          </button>
        </div>

        {/* Row 2: Todos os Responsáveis */}
        {responsaveis.length > 0 && (
          <div className="mt-4 space-y-3">
            {responsaveis.map((r, idx) => (
              <div key={r.id || idx} className="p-4 rounded-xl bg-blue-50/60 border border-blue-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <span className="text-xl font-bold text-blue-400 mt-0.5">•</span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-blue-600" data-testid={`header-guardian-role-${r.id || idx}`}>
                        {financeiroVisivel && r.id === (data360?.responsible_financial_id || aluno?.responsible_financial_id) ? 'Responsável Financeiro' : r.id === (data360?.responsible_didactic_id || aluno?.responsible_didactic_id) ? 'Responsável Pedagógico' : 'Responsável vinculado'}
                      </p>
                      <p className="text-base font-bold text-slate-800 break-words" data-testid={`header-guardian-name-${r.id || idx}`}>{r.name || 'Nome não informado'}</p>
                    </div>
                  </div>
                  <button onClick={() => { setEditForm({...aluno}); setShowEdit(true); }} title="Editar" className="p-1.5 rounded-lg hover:bg-blue-100 transition-colors">
                    <Edit className="h-4 w-4 text-slate-500" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {r.email && (
                    <a href={`mailto:${r.email}`} className="inline-flex max-w-full items-start gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors text-sm" data-testid={`header-guardian-email-${r.id || idx}`}>
                      <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" /> <span className="break-all">{r.email}</span>
                    </a>
                  )}
                  {(r.whatsapp || r.phone || r.cellphone) && (
                    <a href={`https://wa.me/55${(r.whatsapp || r.phone || r.cellphone || '').replace(/\D/g, '')}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors text-sm border border-emerald-200">
                      <Phone className="h-3.5 w-3.5" /> <SensitiveField verb="telefone" value={r.whatsapp || r.phone || r.cellphone} onceKey={`resp-wa-${r.id}`} targetType="responsavel" targetId={r.id} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* 2. STATUS CARDS */}
      <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-3" variants={stagger}>
        <StatusCard label="Frequência" value={freqPct === '-' ? 'N/A' : `${freqPct}%`} icon={Activity} color={typeof freqPct === 'number' && freqPct < 80 ? '#F59E0B' : '#10B981'} sub={`${freqPresente}/${freqTotal} aulas`} />
        <StatusCard label="Média Geral" value={mediaGeral} icon={TrendingUp} color={mediaGeral !== '-' && parseFloat(mediaGeral) < 6 ? '#EF4444' : '#7B3F98'} sub={`${notas.length} notas lançadas`} />
        {financeiroVisivel && <StatusCard label="Financeiro" value={fin.vencidos > 0 ? `${fin.vencidos} vencidos` : 'Em dia'} icon={Wallet} color={fin.vencidos > 0 ? '#EF4444' : '#10B981'} sub={`${fin.total || 0} títulos`} />}
        <StatusCard label="DNA Go" value={`${dnago.ocorrencias?.length || 0} ocorrências`} icon={Shield} color={dnago.ocorrencias?.length > 0 ? '#F59E0B' : '#0891B2'} sub={`${dnago.ultimas_retiradas?.length || 0} retiradas`} />
      </motion.div>

      {/* 2.4 RESPONSÁVEIS 360 — Card consolidado (Financeiro / Pedagógico / Retirada) — FASE 2 */}
      <motion.div variants={fadeUp}>
        <ResponsaveisConsolidadoCard
          aluno={aluno}
          pickups={
            Array.isArray(aluno?.authorized_pickups) && aluno.authorized_pickups.length > 0
              ? aluno.authorized_pickups
              : (aluno?.authorized_pickup
                  ? [{ name: aluno.authorized_pickup, phone: aluno.authorized_pickup_phone, relation: aluno.authorized_pickup_relation }]
                  : [])
          }
          onEdit={() => { setEditForm({ ...aluno }); setShowEdit(true); }}
        />
      </motion.div>

      {/* 2.45 VÍNCULOS ANUAIS — histórico por ano letivo + ações seguras (V7.2) */}
      <motion.div variants={fadeUp}>
        <VinculosAnuais studentId={aluno?.id} year={selectedYear} onYearChange={setSelectedYear} onChanged={() => load360(aluno)} />
      </motion.div>

      {/* 2.5 MOTOR FINANCEIRO CENTRAL — oculto para perfis sem acesso financeiro */}
      {financeiroVisivel && (
      <motion.div variants={fadeUp}>
        <AlunoFinanceiroTab studentId={aluno?.id} />
      </motion.div>
      )}

      {/* 3. MAIN GRID — Academic + Financial + Communication + Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* COL 1-2: Academic + Financial */}
        <div className="lg:col-span-2 space-y-4">
          {/* ACADEMIC */}
          <motion.div variants={fadeUp} className="rounded-2xl p-5 bg-white border border-slate-100 shadow-sm">
            <SectionHeader icon={GraduationCap} title={`Acadêmico${selectedYear ? ` · ${selectedYear}` : ''}`} color="#7B3F98" action={selectedYear ? `Boletim ${selectedYear}` : 'Ver completo'} onAction={() => navigate(selectedYear ? `/v2/academico/boletim?student_id=${aluno?.id}&year=${selectedYear}` : '/v2/academico/notas')} />
            {notas.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center" data-testid="aluno360-notas-empty">Nenhuma nota lançada{selectedYear ? ` em ${selectedYear}` : ''}</p>
            ) : (
              <div className="space-y-2">
                {notas.slice(0, 5).map((n, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                    <div>
                      <p className="text-[13px] font-medium text-slate-700">{n.subject_name || n.period || `Nota ${i + 1}`}</p>
                      <p className="text-[11px] text-slate-400">{n.period || n.year || ''}</p>
                    </div>
                    <span className={cn("text-lg font-bold", (n.value || 0) >= 6 ? 'text-emerald-600' : (n.value || 0) >= 4 ? 'text-amber-600' : 'text-rose-600')}>
                      {n.value?.toFixed(1) || '-'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* FINANCIAL — oculto para perfis sem acesso financeiro */}
          {financeiroVisivel && (
          <motion.div variants={fadeUp} className="rounded-2xl p-5 bg-white border border-slate-100 shadow-sm">
            <SectionHeader icon={Wallet} title="Financeiro" color="#059669" action="Ver completo" onAction={() => navigate('/v2/financeiro')} />
            {titulos.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-slate-400">Nenhum título financeiro</p>
                {resp && (
                  <div className="mt-3 p-3 rounded-xl bg-slate-50 inline-flex items-center gap-2">
                    <Users className="h-4 w-4 text-slate-400" />
                    <span className="text-[12px] text-slate-600">Responsável: {resp.name || '-'}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {resp && (
                  <div className="p-2.5 rounded-lg bg-slate-50 flex items-center gap-2 mb-3">
                    <Users className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-[12px] text-slate-600">Responsável: <strong>{resp.name}</strong></span>
                    {resp.email && <span className="text-[11px] text-slate-400">({resp.email})</span>}
                  </div>
                )}
                {titulos.slice(0, 5).map((t, i) => (
                  <div key={i} className={cn("flex items-center justify-between py-2 border-b border-slate-50 last:border-0",
                    t.status === 'vencido' && 'bg-rose-50/50 -mx-2 px-2 rounded-lg')}>
                    <div>
                      <p className="text-[13px] font-medium text-slate-700">{t.description || 'Mensalidade'}</p>
                      <p className="text-[11px] text-slate-400">Venc: {fmtDate(t.due_date)}</p>
                    </div>
                    <div className="text-right">
                      <span className={cn("text-sm font-bold", t.status === 'pago' ? 'text-emerald-600' : t.status === 'vencido' ? 'text-rose-600' : 'text-slate-800')}>
                        {fmtCur(t.amount)}
                      </span>
                      <p className={cn("text-[10px] font-semibold", t.status === 'pago' ? 'text-emerald-500' : t.status === 'vencido' ? 'text-rose-500' : 'text-slate-400')}>
                        {t.status === 'pago' ? 'Pago' : t.status === 'vencido' ? 'Vencido' : 'Pendente'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
          )}

          {/* OCORRÊNCIAS PEDAGÓGICAS */}
          <motion.div variants={fadeUp} className="rounded-2xl p-5 bg-white border border-slate-100 shadow-sm">
            <SectionHeader icon={ShieldAlert} title="Ocorrências pedagógicas" color="#7B3F98" />
            <OcorrenciasPanel studentId={aluno?.id} compact />
          </motion.div>

          {/* COMMUNICATION */}
          <motion.div variants={fadeUp} className="rounded-2xl p-5 bg-white border border-slate-100 shadow-sm">
            <SectionHeader icon={MessageSquare} title="Comunicação" color="#0891B2" action="Ver histórico" onAction={() => navigate('/v2/comunicacao')} />
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-xl bg-cyan-50/50 border border-cyan-100">
                <p className="text-[11px] text-slate-500">Notificações</p>
                <p className="text-lg font-bold text-slate-800">{dnaconnect.notificacoes?.length || 0}</p>
              </div>
              <div className="p-3 rounded-xl bg-violet-50/50 border border-violet-100">
                <p className="text-[11px] text-slate-500">DNA Connect</p>
                <p className="text-lg font-bold text-slate-800">{dnaconnect.notificacoes?.filter(n => n.read)?.length || 0} lidas</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* COL 3: Timeline + Insights */}
        <div className="space-y-4">

          {/* DNA INSIGHTS */}
          <motion.div variants={fadeUp} className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm">
            <SectionHeader icon={Sparkles} title="DNA Insights" color="#7B3F98" />
            <div className="space-y-2">
              {insights.length === 0 ? (
                <div className="text-center py-4">
                  <CheckCircle2 className="h-6 w-6 text-emerald-400 mx-auto mb-2" />
                  <p className="text-[12px] text-slate-400">Aluno sem alertas</p>
                </div>
              ) : insights.map((ins, i) => (
                <InsightItem key={i} text={ins.text} action={ins.action} color={ins.color} onClick={() => ins.path && navigate(ins.path)} />
              ))}
            </div>
          </motion.div>

          {/* TIMELINE */}
          <motion.div variants={fadeUp} className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm">
            <SectionHeader icon={Clock} title="Timeline" color="#6B7280" />
            {timeline.length === 0 && !dnago.ultimas_retiradas?.length ? (
              <p className="text-[12px] text-slate-400 text-center py-4">Nenhum evento registrado</p>
            ) : (
              <div className="space-y-0">
                {dnago.ultimas_retiradas?.slice(0, 3).map((r, i) => (
                  <TimelineItem key={`ret-${i}`} icon={DoorOpen} title={`Retirada — ${r.responsavel_nome || 'Responsável'}`} date={r.hora_retirada} color="#0891B2" />
                ))}
                {dnago.ocorrencias?.slice(0, 3).map((o, i) => (
                  <TimelineItem key={`oc-${i}`} icon={AlertCircle} title={`Ocorrência — ${o.tipo || 'Registro'}`} date={o.data} color="#F59E0B" />
                ))}
                {timeline.slice(0, 5).map((t, i) => (
                  <TimelineItem key={`tl-${i}`} icon={Activity} title={t.action || t.title || 'Evento'} date={t.created_at} color="#7B3F98" />
                ))}
              </div>
            )}
          </motion.div>

          {/* AUDIT TRAIL — FASE 4 */}
          <motion.div variants={fadeUp} className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm">
            <SectionHeader icon={FileText} title="Documentos" color="#0F766E" />
            <DocumentacaoResumo studentId={aluno?.id} />
            <details className="group" data-testid="doc-arquivos-toggle">
              <summary className="cursor-pointer select-none text-[12px] font-semibold text-slate-500 hover:text-slate-700">Arquivos e origem dos dados</summary>
              <div className="mt-2">
                <DocumentosCard studentId={aluno?.id} studentName={aluno?.name}
                  onReviewed={() => { setCompRefresh((n) => n + 1); if (aluno?.id) load360(aluno); }} />
              </div>
            </details>
          </motion.div>

          <motion.div variants={fadeUp}>
            <GoogleAcessoCard studentId={aluno?.id} />
          </motion.div>

          <motion.div variants={fadeUp}>
            <AuditTrailCard studentId={aluno?.id} />
          </motion.div>
        </div>
      </div>

      {/* Search modal */}
      <Dialog open={showSearch} onOpenChange={setShowSearch}>
        <DialogContent>
          <DialogHeader><DialogTitle>Buscar Aluno</DialogTitle></DialogHeader>
          <Input placeholder="Buscar aluno, matrícula, turma ou responsável..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          <div className="max-h-[300px] overflow-y-auto space-y-1 mt-2">
            {filtered.slice(0, 15).map(a => (
              <button key={a.id} onClick={() => selectAluno(a)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">{(a.name||'?')[0]}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{a.name}</p>
                  <p className="text-[11px] text-slate-400">{a.registration_number || '-'}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit + Novo Aluno — cadastro em tela única */}
      <SmartImportModal
        open={showSmartImport}
        onOpenChange={setShowSmartImport}
        studentId={aluno?.id}
        studentName={aluno?.name}
        onApplied={() => { setCompRefresh((n) => n + 1); if (aluno?.id) { load360(aluno); loadResponsibles(); } }}
      />
      <StudentSinglePage
        open={showEdit}
        onOpenChange={(v) => { setShowEdit(v); if (!v && aluno?.id) load360(aluno); }}
        mode="edit"
        student={editForm}
        classGroups={classGroups}
        responsibles={responsibles}
        onSaved={(saved, opts) => {
          loadAlunos();
          // V9.7 · com o cadastro aberto NÃO recarregamos o 360 (remontava a tela e perdia o scroll)
          if (aluno?.id && !opts?.keepOpen) load360(aluno);
        }}
        onResponsiblesUpdate={loadResponsibles}
      />
      <StudentSinglePage
        open={showNewStudent}
        onOpenChange={setShowNewStudent}
        mode="new"
        classGroups={classGroups}
        responsibles={responsibles}
        onSaved={() => { loadAlunos(); }}
        onResponsiblesUpdate={loadResponsibles}
      />

      {/* Dialog Transferir Aluno */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-orange-600" />
              Transferir Aluno de Turma
            </DialogTitle>
            <DialogDescription>
              Transferir <strong>{aluno?.name}</strong> da turma <strong>{aluno?.class_name || data360?.class_name || '-'}</strong> para outra turma.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nova Turma *</Label>
              <Select value={transferFormData.new_class_group_id} onValueChange={v => setTransferFormData(p => ({ ...p, new_class_group_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione a turma de destino" /></SelectTrigger>
                <SelectContent>
                  {classGroups.filter(cg => cg.id !== aluno?.class_group_id).map(cg => (
                    <SelectItem key={cg.id} value={cg.id}>{cg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>N de Chamada na Nova Turma (opcional)</Label>
              <Input type="number" min="1" placeholder="Deixe vazio para gerar depois" value={transferFormData.new_call_number} onChange={e => setTransferFormData(p => ({ ...p, new_call_number: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Motivo (opcional)</Label>
              <Textarea placeholder="Ex: Pedido da familia, adaptacao pedagogica..." value={transferFormData.reason || ''} onChange={e => setTransferFormData(p => ({ ...p, reason: e.target.value }))} rows={2} />
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
              <p className="text-sm font-medium text-blue-800">Transferir historico:</p>
              <div className="flex items-center gap-2">
                <Checkbox id="t-grades" checked={transferFormData.keep_grades} onCheckedChange={c => setTransferFormData(p => ({ ...p, keep_grades: c }))} />
                <Label htmlFor="t-grades" className="text-sm cursor-pointer">Manter notas e boletim</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="t-attendance" checked={transferFormData.keep_attendance} onCheckedChange={c => setTransferFormData(p => ({ ...p, keep_attendance: c }))} />
                <Label htmlFor="t-attendance" className="text-sm cursor-pointer">Manter registros de presenca/falta</Label>
              </div>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-800"><strong>Atencao:</strong> Esta acao nao pode ser desfeita automaticamente.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setTransferDialogOpen(false)} disabled={transferLoading}>Cancelar</Button>
            <Button onClick={handleTransferStudent} disabled={transferLoading || !transferFormData.new_class_group_id} className="bg-orange-600 hover:bg-orange-700">
              {transferLoading ? 'Transferindo...' : 'Confirmar Transferencia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Historico de Transferencias */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Historico de Transferencias
            </DialogTitle>
            <DialogDescription>
              Historico de mudancas de turma de <strong>{aluno?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {historyLoading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
                <p className="text-sm text-slate-500 mt-2">Carregando...</p>
              </div>
            ) : transferHistory.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <History className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Nenhuma transferencia registrada</p>
                <p className="text-xs text-slate-400 mt-1">O aluno esta na turma original desde a matricula</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 font-medium mb-1">Turma Atual</p>
                  <span className="font-semibold">{aluno?.class_name || data360?.class_name || '-'}</span>
                </div>
                <p className="text-xs font-medium text-slate-500">Transferencias ({transferHistory.length})</p>
                {transferHistory.map((t, i) => (
                  <div key={t.id || i} className="p-3 bg-white border rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1">
                        <p className="text-xs text-slate-500">De:</p>
                        <p className="text-sm font-medium">{t.from_class_name || 'N/A'}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-slate-500">Para:</p>
                        <p className="text-sm font-medium">{t.to_class_name || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 border-t pt-2 mt-2">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtTransferDate(t.transfer_date)}</span>
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{t.transferred_by_name || t.transferred_by || '-'}</span>
                    </div>
                    {t.reason && <p className="text-xs text-slate-400 mt-1 italic">Motivo: {t.reason}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  FASE 2 — Card consolidado "Responsáveis 360"
//  Financeiro / Pedagógico / Autorizados a Retirar
// ═══════════════════════════════════════════════════════════════════════
const REQ_BANK = ['cpf','zip_code','address','address_number','neighborhood','city','state'];
const respIncomplete = (r) => !r || REQ_BANK.some(k => !((r?.[k] || '').toString().trim()));

const ROLE_BADGES = [
  ['is_financial', 'Financeiro', 'bg-violet-100 text-violet-700 border-violet-200'],
  ['is_pedagogico', 'Pedagógico', 'bg-emerald-100 text-emerald-700 border-emerald-200'],
  ['is_legal', 'Legal', 'bg-sky-100 text-sky-700 border-sky-200'],
  ['is_primary', 'Contato principal', 'bg-amber-100 text-amber-800 border-amber-200'],
  ['can_pickup', 'Pode retirar', 'bg-blue-100 text-blue-700 border-blue-200'],
];

const ResponsaveisConsolidadoCard = ({ aluno, pickups = [], onEdit }) => {
  // V9.14 · FONTE ÚNICA: TODOS os guardian_student_links do aluno (nunca só o financeiro)
  const [links, setLinks] = useState([]);
  const rank = (l) => (l.is_financial ? 0 : l.is_pedagogico ? 1 : l.is_legal ? 2 : l.is_primary ? 3 : 4);
  const ordered = [...links].sort((a, b) => rank(a) - rank(b));
  const extraPickups = (pickups || []).filter(p => p && (p.name || '').trim());
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5" data-testid="responsaveis-consolidado">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">Responsáveis</h3>
          {ordered.length > 0 && (
            <span className="text-[10px] font-bold bg-slate-100 text-slate-600 rounded px-1.5 py-0.5"
              data-testid="resp-cons-count">{ordered.length} vinculado(s)</span>
          )}
        </div>
        <button onClick={onEdit} className="text-[11px] text-violet-600 hover:text-violet-800 flex items-center gap-1" data-testid="resp-cons-edit">
          <Edit className="w-3 h-3" /> Editar
        </button>
      </div>

      {ordered.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic" data-testid="resp-cons-empty">Nenhum responsável vinculado</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {ordered.map((l) => {
            const g = l.guardian || {};
            const phone = g.cellphone || g.phone || g.whatsapp;
            const incomplete = l.is_financial && respIncomplete(g);
            return (
              <div key={l.responsible_id}
                className={`rounded-xl p-3 border ${incomplete ? 'bg-amber-50/50 border-amber-200'
                  : l.is_financial ? 'bg-violet-50/50 border-violet-200' : 'bg-slate-50/70 border-slate-200'}`}
                data-testid={`resp-cons-card-${l.responsible_id}`}>
                <div className="flex flex-wrap gap-1 mb-1">
                  {ROLE_BADGES.filter(([k]) => l[k]).map(([k, label, cls]) => (
                    <span key={k} className={`text-[9.5px] font-bold uppercase tracking-wide border rounded px-1.5 py-0.5 ${cls}`}>{label}</span>
                  ))}
                  {!ROLE_BADGES.some(([k]) => l[k]) && (
                    <span className="text-[9.5px] font-bold uppercase tracking-wide border rounded px-1.5 py-0.5 bg-slate-100 text-slate-500 border-slate-200">Sem papel</span>
                  )}
                </div>
                <p className="text-sm font-bold text-slate-800 break-words" data-testid={`overview-guardian-name-${l.responsible_id}`}>{g.name || 'Nome não informado'}</p>
                <p className="text-[11px] text-slate-500 break-words" data-testid={`overview-guardian-relation-${l.responsible_id}`}>
                  {l.relation || 'Responsável'}
                  {g.cpf ? <> · <SensitiveField verb="cpf" value={g.cpf} onceKey={`resp-cpf-${g.id}`} targetType="responsavel" targetId={g.id} /></> : ' · sem CPF'}
                </p>
                <div className="flex flex-col gap-0.5 mt-1.5 text-[11px]">
                  {g.email && <span className="text-slate-600 flex items-start gap-1 min-w-0" data-testid={`overview-guardian-email-${l.responsible_id}`}><Mail className="w-3 h-3 shrink-0 mt-0.5" /><span className="break-all">{g.email}</span></span>}
                  {phone && (
                    <span className="text-slate-600 flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      <SensitiveField verb="telefone" value={phone} onceKey={`resp-phone-${g.id}`} targetType="responsavel" targetId={g.id} />
                    </span>
                  )}
                </div>
                <div className="flex gap-1 mt-2">
                  {phone && (
                    <a href={`https://wa.me/55${(phone || '').replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
                      className="text-[10px] font-bold text-emerald-700 border border-emerald-200 bg-emerald-50 rounded px-1.5 py-0.5"
                      data-testid={`resp-cons-wa-${l.responsible_id}`}>WhatsApp</a>
                  )}
                  {g.email && (
                    <a href={`mailto:${g.email}`}
                      className="text-[10px] font-bold text-sky-700 border border-sky-200 bg-sky-50 rounded px-1.5 py-0.5"
                      data-testid={`resp-cons-mail-${l.responsible_id}`}>E-mail</a>
                  )}
                </div>
                {incomplete && (
                  <div className="mt-2 pt-2 border-t border-amber-200 flex items-start gap-1 text-[10.5px] text-amber-900" data-testid="resp-cons-fin-warn">
                    <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>Cadastro incompleto · Boleto Safra bloqueado</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {extraPickups.length > 0 && (
        <div className="mt-3 rounded-xl p-3 border border-blue-200 bg-blue-50/50" data-testid="resp-cons-retirada">
          <p className="text-[10px] uppercase tracking-wider font-bold text-blue-700 flex items-center gap-1">
            <DoorOpen className="w-3 h-3" /> Outros autorizados a retirar
            <span className="ml-auto text-[10px] bg-blue-100 text-blue-700 rounded px-1.5">{extraPickups.length}</span>
          </p>
          <div className="space-y-1 mt-1">
            {extraPickups.slice(0, 4).map((p, i) => (
              <div key={i} className="text-[11px]">
                <span className="font-semibold text-slate-700">{p.name}</span>
                {p.relation && <span className="text-slate-500"> · {p.relation}</span>}
                {p.phone && <span className="text-slate-500"> · {p.phone}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* V9.7 · todos os vínculos reais do aluno (um responsável, vários filhos) */}
      {aluno?.id && (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <GuardiansLinkedList studentId={aluno.id} studentName={aluno.name} student={aluno}
            onEditGuardian={onEdit} onLoaded={setLinks} />
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
//  FASE 4 — Card de Histórico de Alterações (audit trail)
// ═══════════════════════════════════════════════════════════════════════
const AUDIT_FIELD_LABELS = {
  name: 'Nome', cpf: 'CPF', rg: 'RG', registration_number: 'Matrícula',
  class_group_id: 'Turma', class_name: 'Turma (nome)', year: 'Ano',
  situation: 'Situação', birth_date: 'Nascimento', gender: 'Gênero',
  email: 'E-mail', phone: 'Telefone', cellphone: 'Celular', whatsapp: 'WhatsApp',
  address: 'Endereço', address_number: 'Número', neighborhood: 'Bairro',
  city: 'Cidade', state: 'UF', zip_code: 'CEP',
  responsible_id: 'Resp. Financeiro', responsible_didactic_id: 'Resp. Pedagógico',
  responsible_financial_optional_id: 'Resp. Fin. Opcional',
  authorized_pickup: 'Autorizado (legado)', authorized_pickups: 'Autorizados a retirar',
  blood_type: 'Tipo sanguíneo', allergies: 'Alergias', diseases: 'Doenças',
  disabilities: 'Deficiências', financial_plans: 'Planos financeiros',
  active: 'Ativo', pedagogical_notes: 'Obs. pedagógicas',
  classification: 'Classificação', call_number: 'Nº de chamada',
};

function AuditTrailCard({ studentId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!studentId) return;
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/students/${studentId}/audit-trail?limit=20`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (alive) setItems(Array.isArray(data?.items) ? data.items : []);
      } catch { if (alive) setItems([]); } finally { if (alive) setLoading(false); }
    };
    load();
    return () => { alive = false; };
  }, [studentId]);

  if (loading) {
    return (
      <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm" data-testid="audit-trail-card">
        <SectionHeader icon={History} title="Histórico de alterações" color="#475569" />
        <div className="flex items-center gap-2 text-xs text-slate-400 py-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando…</div>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm" data-testid="audit-trail-card">
        <SectionHeader icon={History} title="Histórico de alterações" color="#475569" />
        <p className="text-[12px] text-slate-400 text-center py-3">Nenhuma alteração registrada</p>
      </div>
    );
  }
  const visible = expanded ? items : items.slice(0, 3);
  return (
    <div className="rounded-2xl p-4 bg-white border border-slate-100 shadow-sm" data-testid="audit-trail-card">
      <SectionHeader icon={History} title={`Histórico de alterações (${items.length})`} color="#475569" />
      <div className="space-y-2 mt-1">
        {visible.map((ev) => {
          const changedKeys = new Set([...Object.keys(ev.before || {}), ...Object.keys(ev.after || {})]);
          const ts = ev.timestamp ? new Date(ev.timestamp).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '';
          return (
            <div key={ev.id} className="border-l-2 border-violet-300 pl-3 py-1" data-testid={`audit-item-${ev.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[11.5px] font-semibold text-slate-700">
                    {ev.event_type === 'STUDENT_CREATED' ? 'Aluno criado' : ev.event_type === 'STUDENT_UPDATED' ? 'Atualização' : ev.event_type}
                  </p>
                  <p className="text-[10px] text-slate-400">{ts} · {ev.user_email || 'sistema'}</p>
                </div>
              </div>
              {changedKeys.size > 0 && (
                <div className="mt-1 space-y-0.5">
                  {Array.from(changedKeys).slice(0, 5).map((k) => {
                    const before = ev.before?.[k];
                    const after = ev.after?.[k];
                    const labelK = AUDIT_FIELD_LABELS[k] || k;
                    const fmt = (v) => {
                      if (v == null || v === '') return '—';
                      if (Array.isArray(v)) return `${v.length} item(ns)`;
                      if (typeof v === 'object') return JSON.stringify(v).slice(0, 40);
                      return String(v).slice(0, 60);
                    };
                    return (
                      <div key={k} className="text-[10.5px] flex items-center gap-1.5 flex-wrap">
                        <span className="text-slate-500 font-medium">{labelK}:</span>
                        <span className="text-rose-600 line-through">{fmt(before)}</span>
                        <ArrowRight className="w-2.5 h-2.5 text-slate-400" />
                        <span className="text-emerald-700 font-semibold">{fmt(after)}</span>
                      </div>
                    );
                  })}
                  {changedKeys.size > 5 && <p className="text-[10px] text-slate-400">+{changedKeys.size - 5} campo(s)</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {items.length > 3 && (
        <button onClick={() => setExpanded(e => !e)} className="text-[11px] text-violet-600 hover:text-violet-800 font-semibold mt-2" data-testid="audit-toggle">
          {expanded ? 'Ver menos' : `Ver todos (${items.length})`}
        </button>
      )}
    </div>
  );
}

